import type { ParsedQuery } from "query-string";

export interface BookDeepLinkParams {
    articleUuid: string;
    /** Empty when the link carries no unit. */
    unitFromUuid: string;
    /** Empty for single-unit links. */
    unitToUuid: string;
}

const STORAGE_KEY_ARTICLE_UUID = "initUAID";
const STORAGE_KEY_UNIT_FROM_UUID = "initFromUUID";
const STORAGE_KEY_UNIT_TO_UUID = "initToUUID";

/** A repeated param arrives as an array; the first non-empty value wins. */
const asString = (value: ParsedQuery[string]): string => {
    const values = Array.isArray(value) ? value : [value];
    return values.find((v): v is string => typeof v === "string" && v !== "") ?? "";
};

/**
 * `unitUuid` (legacy spelling of `unitFromUuid`) wins when both are present.
 * @returns null when `articleUuid` is missing.
 */
export const parseBookDeepLinkParams = (urlParams: ParsedQuery): BookDeepLinkParams | null => {
    const articleUuid = asString(urlParams.articleUuid);
    if (!articleUuid) return null;

    return {
        articleUuid,
        unitFromUuid: asString(urlParams.unitUuid) || asString(urlParams.unitFromUuid),
        unitToUuid: asString(urlParams.unitToUuid),
    };
};

const setOrRemove = (key: string, value: string): void => {
    if (value) {
        localStorage.setItem(key, value);
    } else {
        localStorage.removeItem(key);
    }
};

/** Survives the login redirect; empty unit fields clear stale keys from an earlier link. */
export const storeBookDeepLinkParams = ({ articleUuid, unitFromUuid, unitToUuid }: BookDeepLinkParams): void => {
    localStorage.setItem(STORAGE_KEY_ARTICLE_UUID, articleUuid);
    setOrRemove(STORAGE_KEY_UNIT_FROM_UUID, unitFromUuid);
    setOrRemove(STORAGE_KEY_UNIT_TO_UUID, unitToUuid);
};

export const hasStoredBookDeepLink = (): boolean => !!localStorage.getItem(STORAGE_KEY_ARTICLE_UUID);

/** Clears the stored link on read so a reload does not replay it; missing values come back as empty strings. */
export const takeStoredBookDeepLinkParams = (): BookDeepLinkParams => {
    const params = {
        articleUuid: localStorage.getItem(STORAGE_KEY_ARTICLE_UUID) || "",
        unitFromUuid: localStorage.getItem(STORAGE_KEY_UNIT_FROM_UUID) || "",
        unitToUuid: localStorage.getItem(STORAGE_KEY_UNIT_TO_UUID) || "",
    };
    localStorage.removeItem(STORAGE_KEY_ARTICLE_UUID);
    localStorage.removeItem(STORAGE_KEY_UNIT_FROM_UUID);
    localStorage.removeItem(STORAGE_KEY_UNIT_TO_UUID);
    return params;
};
